import { generatePath, matchPath } from 'react-router';
import { put, select, takeEvery, call, all } from 'redux-saga/effects';

import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { MAX_RECORD } from 'constants/Global';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { fetchDealers } from 'modules/dealers/actions';
import { DEFAULT_DEALERS_FIELDS } from 'modules/dealers/constants';
import { fetchGate } from 'modules/gates/actions/core';
import type { ApiFailureAction } from 'modules/global/generic.types';
import * as actions from 'modules/kiosks/actions';
import * as types from 'modules/kiosks/actionTypes';
import { DETAILS_SOURCE } from 'modules/kiosks/constants';
import type { CreateKioskSuccessAction, DetailedKiosk, PutKioskSuccessAction } from 'modules/kiosks/types';
import { navigate } from 'modules/router/navigate';
import { fetchScreensaver } from 'modules/screensaver/actions/core';
import { createNotification } from 'modules/toast-notifications/actions';
import { getCurrentLocation } from 'selectors/router';

const PUT_ACTION_TYPES: Array<string> = [types.PUT_KIOSK_SUCCESS, types.PUT_KIOSK_FAILURE];

function* afterUpdateWorker(action: PutKioskSuccessAction | CreateKioskSuccessAction | ApiFailureAction) {
  const { type: actionType, error } = action;
  const type = error ? 'error' : 'success';
  const operation = PUT_ACTION_TYPES.includes(actionType) ? 'update' : 'create';

  const message = error ? {
    id: `page.kiosks.${operation}.failure`,
    defaultMessage: `An error occurred during the kiosk ${operation}.`,
  } : {
    id: `page.kiosks.${operation}.success`,
    defaultMessage: `The kiosk has been ${operation}d with success`,
  };

  if (!error && operation === 'create') {
    yield call(navigate, generatePath(PATH.KIOSKS_DETAILS.path, { kioskId: action.payload.id }), { replace: true });
  }

  yield put(createNotification({ type, message }));
}

function* initializeDetailsWorker() {
  const pathname: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const { kioskId } = matchPath(PATH.KIOSKS_DETAILS.path, pathname)?.params ?? {};

  if (kioskId && kioskId !== NEW_ENTITY_ID) {
    const response = yield* putAndWait<DetailedKiosk>(actions.fetchKiosk(kioskId, { source: DETAILS_SOURCE }));

    if (!response.error) {
      const callbackActions = [];
      const { payload: kiosk } = response;
      const dealerIds = kiosk.dealers?.map(dealer => dealer.id);

      if (dealerIds && dealerIds.length > 0) {
        callbackActions.push(putAndWait(
          fetchDealers(undefined, {
            ids: [...dealerIds],
            size: MAX_RECORD,
            fields: `${DEFAULT_DEALERS_FIELDS},config,config.*`,
          })
        ));
      }

      if (kiosk.screensaverId) {
        callbackActions.push(putAndWait(fetchScreensaver(kiosk.screensaverId)));
      }

      if (kiosk.gateId) {
        callbackActions.push(putAndWait(fetchGate(kiosk.gateId, {})));
      }
      yield all(callbackActions);
    }
  }

  yield put(actions.setIsReady());
}

function* onDetailsWorker() {
  yield call(initializeDetailsWorker);

  yield takeEvery([types.PUT_KIOSK_SUCCESS, types.PUT_KIOSK_FAILURE], afterUpdateWorker);
  yield takeEvery([types.POST_KIOSK_SUCCESS, types.POST_KIOSK_FAILURE], afterUpdateWorker);
}

export default function* kiosksDetailsSaga() {
  yield scopeToPage([PATH.KIOSKS_DETAILS], onDetailsWorker);
}
